import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  Alert,
  AutoComplete,
  Form,
  Input,
  Select,
  Spin,
  message,
  Drawer,
  Button,
  Checkbox,
  Collapse,
  Tooltip,
} from 'antd';
import DatePicker from '../../components/DatePicker';
import MATCH_KIND_LABELS from '../../components/patient-search/patientSearchMatchKinds';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import {
  User,
  FileText,
  ListChecks,
  ListBullets,
  Folder,
  ClipboardText,
  FolderSimplePlus,
  CalendarCheck,
} from '@phosphor-icons/react';
import axios from 'axios';
import PatientSearchPanel from '../../components/patient-search/PatientSearchPanel';
import EmrProviderAutocomplete from '../../components/autocomplete/EmrProviderAutocomplete';
import EhrPhoneInput from './EhrPhoneInput';
import AthenaEncounterSelector from '../../components/athena/AthenaEncounterSelector';
import NextgenDocumentMetadataFields from '../../components/nextgen/NextgenDocumentMetadataFields';
import AppointmentHistory from '../../components/ehr/AppointmentHistory';
import appointmentHistorySource from '../../components/ehr/appointmentHistorySources';
import ReferringProviderRequestModal from '../../components/nextgen/ReferringProviderRequestModal';
import { canHoldDocument, describeHold, placeAwaitingProviderHold } from '../../components/inbox/awaitingProvider';
import NextgenTaskTemplateFields, { templateSubmission } from '../../components/nextgen/NextgenTaskTemplateFields';
import useNextgenTaskTemplates from '../../components/nextgen/useNextgenTaskTemplates';
import useEhrDrawerPatientSearch from '../../hooks/useEhrDrawerPatientSearch';
import AthenaInsuranceSection from '../../components/athena/AthenaInsuranceSection';
import { createChartUpload, requestInboxTableRefresh } from '../../actions/records';
import { normalizeRequestError } from '../../actions/helpers/requestTransport';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../../components/chartActions/replyTiffWarning';
import { getDocumentCategories } from '../../actions/userProfile';
import { captureAnalytics } from '../../actions/preload';
import {
  ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE, hasFeatureInLocalStorage,
} from '../../global/featureFlags';
import dedupeDocumentCategories from '../../utils/dedupeDocumentCategories';
import {
  assigneeFormValue, hasPresentValue, normalizeFormValues, normalizePhoneDigits, validateCrossFieldRules,
} from '../../util/ehr';
import { getFieldFlexStyle, getFieldWidthClass, getRowClass } from '../../util/fieldGrouping';
import {
  derivePatientIdentifierDisplay,
  extractPatientDob,
  extractPatientMrn,
  getSectionIconType,
  getWorkflowIconType,
  matchesDobField,
  parseDateFieldValue,
  resolveFieldSlots,
  savedFormPrefillValues,
} from './dynamicEhrDrawerHelpers';
import './dynamicEhrDrawer.scss';

const { TextArea } = Input;

// Date spellings each prefill path accepts. Deliberately different lists: the
// patient-search mapping has only ever seen ISO and US dates, while the schema
// prefill also accepts a bare YYYY-MM-DD. Kept separate so the shared parser
// does not silently widen either one.
const PATIENT_DATE_FORMATS = [moment.ISO_8601, 'MM/DD/YYYY'];
const PREFILL_DATE_FORMATS = [moment.ISO_8601, 'MM/DD/YYYY', 'YYYY-MM-DD'];

const generateIdempotencyKey = () => {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const applyPatientFallbacks = (patient, baseUpdates, fieldDefinitions, identifierOverridesRef) => {
  if (!patient) {
    return baseUpdates;
  }

  const updates = { ...baseUpdates };

  const assignValueToMatchingFields = (matcher, rawValue) => {
    if (rawValue === undefined || rawValue === null) {
      return;
    }

    const valueString = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    if (valueString === '') {
      return;
    }

    Object.values(fieldDefinitions).forEach((field) => {
      if (!matcher(field)) {
        return;
      }
      if (updates[field.internal_name] !== undefined) {
        return;
      }

      if (field.component === 'date') {
        updates[field.internal_name] = parseDateFieldValue(valueString, PATIENT_DATE_FORMATS);
        return;
      }

      updates[field.internal_name] = valueString;
    });
  };

  // Apply DOB fallback if not already mapped
  assignValueToMatchingFields(matchesDobField, extractPatientDob(patient));

  if (updates.patient_identifier === undefined) {
    const patientId = patient?.value;
    const mrnValue = extractPatientMrn(patient);
    const displayValue = derivePatientIdentifierDisplay(patientId, mrnValue);

    if (displayValue !== undefined && displayValue !== null) {
      updates.patient_identifier = displayValue;
      if (identifierOverridesRef?.current && patientId !== undefined && patientId !== null) {
        identifierOverridesRef.current.patient_identifier = {
          id: patientId,
          display: displayValue,
        };
      }
    }
  }

  return updates;
};

const clearValuesForFields = (fieldValues) => Object.keys(fieldValues || {}).reduce((acc, key) => {
  acc[key] = undefined;
  return acc;
}, {});

// Map section titles to appropriate icons
const getSectionIcon = (sectionTitle) => {
  switch (getSectionIconType(sectionTitle)) {
    case 'patient':
      return <User size={18} weight="bold" />;
    case 'document':
      return <FileText size={18} weight="bold" />;
    case 'folder':
      return <Folder size={18} weight="bold" />;
    default:
      return <ListBullets size={18} weight="bold" />;
  }
};

// Get icon for the drawer title based on workflow type
const getWorkflowIcon = (workflow, title) => {
  if (getWorkflowIconType(workflow, title) === 'task') {
    return <ListChecks size={24} weight="fill" />;
  }
  if (getWorkflowIconType(workflow, title) === 'create') {
    return <FolderSimplePlus size={24} weight="fill" />;
  }
  // Default to Send to Chart icon
  return <ClipboardText size={24} weight="fill" />;
};

const WORKFLOW_SUPPORT_KEYS = {
  create_chart: 'create_chart',
  send_to_chart: 'send_to_chart',
  task_creation: 'task_creation',
};

const WORKFLOW_LABELS = {
  create_chart: 'Create Chart',
  send_to_chart: 'Send to Chart',
  task_creation: 'Create Task',
};

const TASK_EMBEDDING_WORKFLOWS = ['send_to_chart', 'create_chart'];

const DRAWER_Z_INDEX = 2000;

const isTaskSchemaEntry = (entry) => (
  entry?.task_section === true || entry?.task_section === 'true'
);

// A checkbox puts its label inside antd's own <label>, so this renders the help
// icon as a real button: browsers skip label activation when the click lands on
// interactive content, so the icon does not toggle the box, and it stays
// keyboard reachable. Only the checkbox branch needs it — every other field's
// help icon sits in the Form.Item label, outside any control.
const renderCheckboxHelpIcon = (helpText) => (
  <Tooltip title={helpText} getPopupContainer={() => document.body} overlayClassName="ehr-drawer-tooltip">
    <button
      type="button"
      className="dynamic-ehr-drawer__help-icon dynamic-ehr-drawer__help-icon--inline"
      aria-label={helpText}
    >
      &#9432;
    </button>
  </Tooltip>
);

// Schema component that renders the NextGen document-type picker and the
// metadata fields that type declares (see NextgenDocumentMetadataFields).
const NEXTGEN_METADATA_COMPONENT = 'nextgen_document_metadata';
const ATHENA_ENCOUNTER_COMPONENT = 'athena_encounter_selector';
const ATHENA_PROGRESS_NOTE_CATEGORY = 'Encounter Document - Progress Note';
const ATHENA_PROGRESS_NOTE_HIDDEN_FIELDS = new Set([
  'provider_id', 'autoclose', 'status', 'document_status', 'label', 'assignee',
]);
const ATHENA_INSURANCE_COMPONENT = 'athena_insurance';

// Components the drawer renders itself. Anything else in a schema is a field
// this build does not know how to display, and rendering it as a plain text
// input would silently submit the wrong shape.
const KNOWN_FREE_TEXT_COMPONENTS = ['text', undefined, null];

// Components with their own branch in renderField's switch. Together with
// KNOWN_FREE_TEXT_COMPONENTS (which the default branch accepts) this is
// everything this build can display.
const RENDERED_COMPONENTS = [
  'textarea', 'checkbox', 'date', 'select', 'multi_select', 'emr_provider_autocomplete',
  'hidden', NEXTGEN_METADATA_COMPONENT, ATHENA_ENCOUNTER_COMPONENT, ATHENA_INSURANCE_COMPONENT,
];

// A NextGen practice list narrowed to nothing is not an empty dropdown to shrug
// at: nothing can be uploaded until a Super Admin approves a catalog, so say so
// in the same terms the metadata editor uses for its own dead ends.
const NEXTGEN_PRACTICE_EMPTY_STATE = 'No practices are ready for upload. Ask a Super Admin to refresh and approve a practice catalog.';

const isHiddenField = (field) => (
  field?.component === 'hidden' || field?.hidden === true || field?.hidden === 'true'
);

const schemaEntryRows = (entry) => (
  (entry?.subsections || []).flatMap((subsection) => subsection.rows || [])
);

const fieldsFromRows = (rows = []) => rows.flatMap((row) => (
  (row || []).filter(Boolean)
));

const taskFieldsForSections = (sections = []) => {
  const fields = [];

  sections.forEach((section) => {
    if (isTaskSchemaEntry(section)) {
      fields.push(...fieldsFromRows(schemaEntryRows(section)));
      return;
    }

    (section.subsections || []).forEach((subsection) => {
      if (isTaskSchemaEntry(subsection)) {
        fields.push(...fieldsFromRows(subsection.rows || []));
      }
    });
  });

  return fields;
};

const visibleRowsForSubsection = (subsection) => (
  (subsection.rows || [])
    .map(row => (row || []).filter(field => !isHiddenField(field)))
    .filter(row => row.length > 0)
);

const taskFieldNamesForSections = (sections = []) => {
  const names = new Set();

  taskFieldsForSections(sections).forEach((field) => {
    if (field.internal_name) {
      names.add(field.internal_name);
    }
  });

  return names;
};

const requiredFieldsForSections = (sections = []) => (
  sections.flatMap((section) => (
    (section.subsections || []).flatMap((subsection) => (
      (subsection.rows || []).flatMap((row) => (
        (row || []).filter(field => field?.required && field.internal_name && !isHiddenField(field))
      ))
    ))
  ))
);

const credentialSupportsWorkflow = (credential, workflow) => {
  const requiredWorkflow = WORKFLOW_SUPPORT_KEYS[workflow];
  if (!requiredWorkflow) return true;

  return Array.isArray(credential?.supports_workflows)
    && credential.supports_workflows.includes(requiredWorkflow);
};

const mergeMappedPatientSearchValues = (payload, values, fieldMappings) => (
  Object.keys(fieldMappings || {}).reduce((mergedPayload, fieldName) => {
    if (Object.prototype.hasOwnProperty.call(payload, fieldName)) {
      return mergedPayload;
    }

    const value = values[fieldName];
    if (!hasPresentValue(value)) {
      return mergedPayload;
    }

    return {
      ...mergedPayload,
      [fieldName]: moment.isMoment(value) ? value.format('YYYY-MM-DD') : value,
    };
  }, payload)
);

// A field whose options come from a closed, authoritative live dictionary
// (options_strict). Organization categories are not valid substitutes, and a
// prefilled value is only valid if it exists in that source. This is a property
// of the field (set in the schema), not of any one EMR.
const usesAuthoritativeOptions = field => Boolean(field.options_source && field.options_strict);

// Options carrying metadata.group (e.g. NextGen "Workgroups" / "Users") render
// under a heading per group, in the order the groups first appear.
const renderGroupedOptions = (fieldOptions) => {
  const renderOption = (option) => (
    <Select.Option key={option.value} value={option.value}>
      {option.label}
    </Select.Option>
  );
  if (!fieldOptions.some((option) => option.metadata?.group)) return fieldOptions.map(renderOption);

  const groups = new Map();
  fieldOptions.forEach((option) => {
    const group = option.metadata?.group || 'Other';
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group).push(option);
  });
  return [...groups.entries()].map(([group, options]) => (
    <Select.OptGroup key={group} label={group}>
      {options.map(renderOption)}
    </Select.OptGroup>
  ));
};

const matchingOptionValue = (options, value) => {
  const normalizedValue = String(value || '').trim().toLowerCase();
  const match = options.find(option => String(option.value || '').trim().toLowerCase() === normalizedValue);

  return match?.value;
};

// A field with suggestions (schema key `suggested_options`) offers the options
// whose metadata.category matches the current value of the field it depends on
// — e.g. document_title suggesting the titles configured for the selected
// document_category — while still accepting free text. Returns null when the
// field has no suggestions config, [] when the dependency is unset or has no
// configured options (both mean "render as plain free text"), and a non-empty
// list to render an autocomplete.
const suggestedOptionsFor = (field, optionsBySource, dependencyValue) => {
  const config = field.suggested_options;
  if (!config) return null;
  if (!dependencyValue) return [];

  const sourceOptions = optionsBySource[config.options_source] || [];
  const normalized = String(dependencyValue).trim().toLowerCase();

  return sourceOptions.filter(
    (option) => String(option.metadata?.category ?? '').trim().toLowerCase() === normalized,
  );
};

const responseHasErrorCode = (result, code) => (
  Array.isArray(result?.response?.data?.errors)
  && result.response.data.errors.some((error) => error?.code === code)
);

const DynamicEhrDrawer = ({
  visible, drawerData, onClose, createChartUpload, getDocumentCategories, allDocumentCategories,
  requestInboxTableRefresh: refreshInbox,
}) => {
  const [form] = Form.useForm();
  const [schema, setSchema] = useState(null);
  const [options, setOptions] = useState({});
  const [optionMetadata, setOptionMetadata] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState({});
  // Synchronous reentrancy guard: `submitting` state only blocks clicks after
  // a re-render, leaving a double-click window during async validation. A
  // duplicate submit here means a duplicate patient chart in the EHR.
  const submittingRef = useRef(false);
  const [credentials, setCredentials] = useState([]);
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [fetchingCredentials, setFetchingCredentials] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [athenaEncounterProof, setAthenaEncounterProof] = useState(undefined);
  const [athenaEncounterReloadKey, setAthenaEncounterReloadKey] = useState(0);
  const patientIdentifierOverridesRef = useRef({});
  const patientSearchResetRef = useRef(null);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [flashingFields, setFlashingFields] = useState({});
  const [crossFieldErrors, setCrossFieldErrors] = useState([]);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [dynamicLoading, setDynamicLoading] = useState({});
  // Options for fields declaring options_scope: location, keyed by field name.
  const [locationScopedOptions, setLocationScopedOptions] = useState({});
  // Task option sources that failed to load, keyed by source, with the message to show.
  const [taskOptionErrors, setTaskOptionErrors] = useState({});
  const locationScopedRef = useRef({ schema: null, location: undefined });
  const [taskRequested, setTaskRequested] = useState(false);
  const [reviewRequested, setReviewRequested] = useState(false);
  const [insuranceValidationError, setInsuranceValidationError] = useState(null);
  const [archiveAfterUpload, setArchiveAfterUpload] = useState(false);
  // ENG-3658: the credential's "request a new referring provider" setup, and
  // the search text the request form was opened from (null while closed).
  const [providerRequestConfig, setProviderRequestConfig] = useState(null);
  const [providerRequestSearch, setProviderRequestSearch] = useState(null);
  const [providerRequestSent, setProviderRequestSent] = useState(false);
  // ENG-3659: the document's Awaiting Provider hold ({ marked_at, marked_by }) and
  // whether the last attempt to place one failed.
  const [awaitingProviderHold, setAwaitingProviderHold] = useState(null);
  const [awaitingProviderFailed, setAwaitingProviderFailed] = useState(false);
  // ENG-3812: whether the picked NextGen document type has the credential's
  // referring provider field, which carries the same request and mark links.
  const [nextgenReferringField, setNextgenReferringField] = useState(false);
  // ENG-3934: the NextGen document envelope complete except the referring
  // provider (undefined until then). An Awaiting Provider hold keeps it.
  const [nextgenHoldDraft, setNextgenHoldDraft] = useState(undefined);
  // The latest builder of the form an Awaiting Provider hold keeps, read when
  // a hold is placed (markAwaitingProvider is memoized).
  const buildAwaitingProviderFormRef = useRef(null);
  // ENG-3934: a held document's saved form ({ values, task_included, marked_at,
  // marked_by }) from the schema, the task values it pre-filled (for the
  // template restore to keep), and the saved form its template was restored for.
  const [savedForm, setSavedForm] = useState(null);
  const savedTaskValuesRef = useRef(null);
  const restoredTemplateForRef = useRef(null);
  // The drawer stays mounted from one document to the next, so a mark belongs
  // to the document it was placed on: the documents with a mark in flight, and
  // the document on screen now (a late result for another one is dropped).
  const markingAwaitingProviderRef = useRef(new Set());
  const awaitingProviderDocumentRef = useRef(null);
  const idempotencyKeyRef = useRef(null);

  // Single write path for programmatic form updates: antd's form store is the
  // source of truth for validation, but `submitDisabled`/`missingRequiredVisibleField`
  // read the `formValues` mirror, and `form.setFieldsValue` does not fire
  // `onValuesChange`. Funnel both writes through here so the store and the mirror
  // can never drift (a missed mirror would silently break submit-button gating).
  const applyFieldValues = useCallback((values) => {
    form.setFieldsValue(values);
    setFormValues(prev => ({ ...prev, ...values }));
  }, [form]);

  const {
    emrIntegrationCredentialId,
    workflow = 'send_to_chart',
    prefill: incomingPrefill,
    parentType,
    sendToken,
    isSentContext,
    providerHints,
  } = drawerData || {};

  const modalTitle = drawerData?.title || 'Send to Chart';

  const prefill = useMemo(() => incomingPrefill || {}, [incomingPrefill]);
  const patientSearchConfig = schema?.metadata?.patient_search;
  const isAthenaProgressNote = schema?.emr?.toString().toLowerCase() === 'athena'
    && workflow === 'send_to_chart'
    && formValues.document_category === ATHENA_PROGRESS_NOTE_CATEGORY;
  const allowResultSelection = patientSearchConfig?.allow_result_selection !== false;
  const requiresPatientResultSelection = Boolean(
    patientSearchConfig?.require_result_selection === true && allowResultSelection,
  );

  // ENG-3780: NextGen task templates fill the Task section and add their fields.
  const captureTemplateAnalytics = useCallback(
    (event, properties) => captureAnalytics(event, { ...properties, workflow }),
    [workflow],
  );
  const nextgenTaskFieldNames = useMemo(() => taskFieldNamesForSections(schema?.sections || []), [schema]);
  const nextgenTaskValues = useMemo(
    () => Object.fromEntries([...nextgenTaskFieldNames].map((name) => [name, formValues[name]])),
    [formValues, nextgenTaskFieldNames],
  );
  const nextgenTaskTemplates = useNextgenTaskTemplates({
    enabled: visible && schema?.emr === 'NextGen' && nextgenTaskFieldNames.size > 0,
    credentialId: selectedCredential?.id,
    schema,
    embedsTask: TASK_EMBEDDING_WORKFLOWS.includes(workflow),
    taskRequested,
    taskSectionVisible: workflow === 'task_creation' || (taskRequested && !isAthenaProgressNote),
    categoryOptions: options['nextgen/task_categories'],
    priorityOptions: options['nextgen/task_priorities'],
    setTaskRequested,
    applyFieldValues,
    notes: formValues.task_notes,
    taskValues: nextgenTaskValues,
    onAnalytics: captureTemplateAnalytics,
  });

  // ENG-3934: put back the task template and answers a held document's saved
  // form had, once the credential's templates are loaded. Once per saved form.
  // A task saved without a template (cleared, or one since deleted) stays
  // without one, and a form saved without a task stays without one: the
  // document type's template must not replace either choice.
  const {
    templates: nextgenTemplateOptions, restore: restoreNextgenTemplate, declineTask: declineNextgenTask,
  } = nextgenTaskTemplates;
  useEffect(() => {
    if (!savedForm || restoredTemplateForRef.current === savedForm || !nextgenTemplateOptions.length) return;

    restoredTemplateForRef.current = savedForm;
    if (!savedForm.task_included) {
      declineNextgenTask();
      return;
    }
    const savedTemplate = savedForm.values?.nextgen_task_template;
    // Forms saved before answers were kept have only the values.
    const answers = savedTemplate?.answers || Object.fromEntries(Object.entries(savedTemplate?.values || {})
      .map(([key, value]) => [key, { value, display: value }]));
    restoreNextgenTemplate(savedTemplate?.id, answers, savedTaskValuesRef.current);
  }, [declineNextgenTask, nextgenTemplateOptions, restoreNextgenTemplate, savedForm]);

  const patientSearch = useEhrDrawerPatientSearch({
    credential: selectedCredential,
    integrationType: schema?.emr,
    config: patientSearchConfig,
    location: formValues.location,
    allowResultSelection,
    requiresPatientResultSelection,
    selectedPatient,
    setSelectedPatient,
    patientIdentifierOverridesRef,
    form,
    applyFieldValues,
  });
  const {
    settings: patientSearchSettings,
    enabled: patientSearchEnabled,
    isNextgenVariantSearch,
    nextgenPracticeRequired,
    fieldValues: patientFieldValues,
    results: patientSearchResults,
    isLoading: patientSearchLoading,
    error: patientSearchError,
    canSearch: patientSearchCanSearch,
    search: searchPatients,
    searchWarnings: patientSearchWarnings,
    hasMore: patientSearchHasMore,
    activeSearchFields: patientSearchActiveFields,
    searchModes: patientSearchModes,
    activeSearchMode: patientSearchActiveMode,
    changeSearchMode: changePatientSearchMode,
    selectedPatientSummaryRef,
    prepareForSchemaLoad,
    acceptSchemaPrefill,
    resetLifecycle: resetPatientSearchLifecycle,
    clearForMappedFormChange,
    changeField: changePatientSearchField,
    resetSearch: resetPatientSearch,
    selectPatient: selectSearchPatient,
    clearPatient: clearSearchPatient,
    recordAbandonment: recordPatientSearchAbandonment,
    recordSubmission: recordPatientSearchSubmission,
  } = patientSearch;

  const getSubmissionIdempotencyKey = useCallback(() => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = generateIdempotencyKey();
    }

    return idempotencyKeyRef.current;
  }, []);

  const resetKeyAfterConfirmedRejection = (error) => {
    const status = error?.response?.status;
    // A lost response may follow a committed upload. Keep its identity on retry,
    // HTTP 409 can refer to an existing upload with a different archive choice.
    // Preserve its key, as for HTTP 408, so correcting the choice replays it.
    if (status >= 400 && status < 500 && ![408, 409].includes(status)) {
      idempotencyKeyRef.current = null;
    }
  };

  // Fetch document categories when drawer opens
  useEffect(() => {
    if (!visible) return;

    const fetchDocumentCategories = async () => {
      try {
        await getDocumentCategories();
      } catch (error) {
        console.error('Failed to fetch document categories', error);
      }
    };

    fetchDocumentCategories();
  }, [visible, getDocumentCategories]);

  // Update local state when document categories are loaded from Redux
  useEffect(() => {
    if (allDocumentCategories?.categories) {
      setDocumentCategories(allDocumentCategories.categories);
    }
  }, [allDocumentCategories]);

  // A schema component this build cannot render is a deploy-skew fact about the
  // schema, so report it once when the schema arrives. renderField's fallback
  // Alert still tells the user; warning from there repeated the message on every
  // keystroke that re-rendered the drawer.
  useEffect(() => {
    if (!schema) return;

    const unsupported = Array.from(new Set(
      (schema.sections || [])
        .flatMap(schemaEntryRows)
        .flatMap((row) => (row || []).filter(Boolean))
        .map((field) => field.component)
        .filter((component) => (
          !RENDERED_COMPONENTS.includes(component) && !KNOWN_FREE_TEXT_COMPONENTS.includes(component)
        )),
    ));

    unsupported.forEach((component) => {
      console.warn(`Unsupported EHR schema component: ${component}`);
    });
  }, [schema]);

  // Fetch credentials when drawer opens
  useEffect(() => {
    if (!visible || !drawerData) return;

    const fetchCredentials = async () => {
      try {
        const response = await axios.get('/api/v1/emr_integration_credentials');
        const creds = response.data.credentials || [];
        const eligibleCredentials = emrIntegrationCredentialId
          ? creds.filter((credential) => String(credential.id) === String(emrIntegrationCredentialId))
          : creds;
        const workflowCredentials = eligibleCredentials.filter((credential) => (
          credentialSupportsWorkflow(credential, workflow)
        ));
        setCredentials(workflowCredentials);

        // Auto-select first (and only) credential per assumption
        if (workflowCredentials.length > 0) {
          setSelectedCredential(workflowCredentials[0]);
        } else {
          const workflowLabel = WORKFLOW_LABELS[workflow] || 'this workflow';
          const errorMessage = eligibleCredentials.length > 0
            ? `No EMR credential supports ${workflowLabel} for this organization`
            : 'No EMR credentials found for this organization';
          message.error(errorMessage);
          onClose();
        }
      } catch (error) {
        console.error('Failed to fetch credentials', error);
        message.error('Unable to load EMR credentials');
        onClose();
      } finally {
        setFetchingCredentials(false);
      }
    };

    fetchCredentials();
  }, [visible, drawerData, emrIntegrationCredentialId, workflow, onClose]);

  // Reset state after drawer close animation completes
  const handleAfterVisibleChange = useCallback((isVisible) => {
    if (!isVisible) {
      setArchiveAfterUpload(false);
      setSchema(null);
      setOptions({});
      setOptionMetadata({});
      setLoading(true);
      setSubmitting(false);
      setFormValues({});
      setNextgenHoldDraft(undefined);
      setSavedForm(null);
      setCredentials([]);
      setSelectedCredential(null);
      setFetchingCredentials(true);
      setSelectedPatient(null);
      setAthenaEncounterProof(undefined);
      setAthenaEncounterReloadKey(0);
      setFlashingFields({});
      setCrossFieldErrors([]);
      setDynamicOptions({});
      setDynamicLoading({});
      setTaskRequested(false);
      setReviewRequested(false);
      setInsuranceValidationError(null);
      patientIdentifierOverridesRef.current = {};
      idempotencyKeyRef.current = null;
      resetPatientSearchLifecycle();
      form.resetFields();
    }
  }, [form, resetPatientSearchLifecycle]);

  // Fetch schema when credential is selected
  useEffect(() => {
    // Don't fetch schema until we have a credential and drawer is visible
    if (!visible || !selectedCredential) {
      return;
    }

    let cancelled = false;
    const credentialId = selectedCredential.id;

    setInsuranceValidationError(null);

    const fetchSchema = async () => {
      setLoading(true);
      prepareForSchemaLoad();
      // ENG-3934: the last document's saved form must not restore its task here
      // when that document's templates arrive late.
      setSavedForm(null);
      savedTaskValuesRef.current = null;
      try {
        const response = await axios.get('/api/v1/emr_ui_schemas', {
          params: {
            emr_integration_credential_id: credentialId,
            workflow,
            parent_type: parentType,
            send_token: sendToken,
          },
        });
        if (cancelled) return;

        const schemaResponse = response.data;
        setSchema(schemaResponse);
        setTaskRequested(false);
        setReviewRequested(false);

        const prefetchedOptions = schemaResponse?.options || {};
        const patientSearchSource = schemaResponse?.metadata?.patient_search?.options_source;

        // Extract all fields from nested structure to get options sources
        const allFieldsForOptions = [];
        (schemaResponse.sections || []).forEach((section) => {
          (section.subsections || []).forEach((subsection) => {
            (subsection.rows || []).forEach((row) => {
              allFieldsForOptions.push(...row);
            });
          });
        });

        const sources = Array.from(new Set(
          [
            ...allFieldsForOptions
              .filter((f) => !f.dynamic_options_source && f.options_scope !== 'location')
              .map(field => field.options_source),
            // Suggestion fields prefetch their (whole) option source up front,
            // then filter it client-side by the dependency value at render time.
            ...allFieldsForOptions
              .map((field) => field.suggested_options?.options_source),
            patientSearchSource,
          ].filter(Boolean),
        ));

        const sourcesToFetch = sources.filter(source => !prefetchedOptions[source]);
        let fetchedOptions = {};
        let fetchedOptionMetadata = {};
        let fetchedTaskOptionErrors = {};

        // Task option lists are only needed once a task is requested. If one fails,
        // its fields show the error and the drawer stays open, so the document can
        // still be filed without a task.
        const taskFields = new Set(taskFieldsForSections(schemaResponse.sections || []));
        const taskSources = new Set();
        const otherSources = new Set([patientSearchSource]);
        allFieldsForOptions.forEach((field) => {
          if (field.options_source) (taskFields.has(field) ? taskSources : otherSources).add(field.options_source);
        });
        const taskOnlySources = new Set([...taskSources].filter((source) => !otherSources.has(source)));

        if (sourcesToFetch.length) {
          const fetchOptions = await Promise.all(sourcesToFetch.map(async (source) => {
            try {
              const res = await axios.get('/api/v1/emr_options', {
                params: {
                  emr_integration_credential_id: credentialId,
                  source,
                },
              });
              return [source, res.data.options || [], res.data];
            } catch (error) {
              if (source === 'veradigm/caregivers') {
                if (!cancelled) message.error('Unable to load Veradigm caregivers. Close and reopen the form to try again.');
                return [source, [], {}];
              }
              if (!taskOnlySources.has(source)) throw error;

              const loadError = error?.response?.data?.error || 'Unable to load these options. Close and reopen the form to try again.';
              return [source, [], {}, loadError];
            }
          }));
          if (cancelled) return;

          fetchedOptions = Object.fromEntries(fetchOptions.map(([source, sourceOptions]) => [source, sourceOptions]));
          fetchedOptionMetadata = Object.fromEntries(fetchOptions.map(([source, , data]) => [source, data]));
          fetchedTaskOptionErrors = Object.fromEntries(fetchOptions
            .flatMap(([source, , , loadError]) => (loadError ? [[source, loadError]] : [])));
        }
        if (cancelled) return;

        setOptionMetadata(fetchedOptionMetadata);
        setTaskOptionErrors(fetchedTaskOptionErrors);

        const allOptions = { ...prefetchedOptions, ...fetchedOptions };
        setOptions(allOptions);

        form.resetFields();

        // Extract all fields from nested sections/subsections/rows and apply prefill
        const initialValues = {};
        // ENG-3934: what the user had entered before setting the document aside
        // as Awaiting Provider wins over every other default. The NextGen
        // document and the task template restore themselves, and a patient that
        // must come from search results is picked again: the names and date of
        // birth filled in here run that search.
        const heldForm = schemaResponse.prefill?.saved_form || null;
        const schemaFields = (schemaResponse.sections || []).flatMap((section) => (
          (section.subsections || []).flatMap((subsection) => (subsection.rows || []).flat())
        )).filter(Boolean);
        const restoredElsewhere = schemaFields
          .filter((field) => field.component === NEXTGEN_METADATA_COMPONENT)
          .map((field) => field.internal_name);
        if (schemaResponse.metadata?.patient_search?.require_result_selection) restoredElsewhere.push('patient_identifier');
        const resolvedPrefill = {
          ...(schemaResponse.prefill || {}),
          ...prefill,
          ...savedFormPrefillValues(heldForm, { omit: restoredElsewhere }),
        };
        (schemaResponse.sections || []).forEach((section) => {
          (section.subsections || []).forEach((subsection) => {
            (subsection.rows || []).forEach((row) => {
              row.forEach((field) => {
                // A saved form's assignees are in the upload's { type, emr_id } shape.
                const value = assigneeFormValue(field, resolvedPrefill[field.internal_name]);
                // A blank prefill is the absence of a value, not a value: it must
                // fall through to auto_select_single/default handling below
                // rather than pinning the field to ''.
                if (hasPresentValue(value)) {
                  const fieldOptions = field.options_source ? (allOptions[field.options_source] || []) : [];
                  const authoritative = usesAuthoritativeOptions(field);
                  const authoritativeValue = authoritative
                    ? matchingOptionValue(fieldOptions, value)
                    : undefined;

                  if (authoritative && authoritativeValue === undefined) {
                    // Value isn't in the authoritative source. If the source
                    // simply hasn't loaded, warn so the drop is diagnosable;
                    // otherwise it's a genuine not-in-dictionary value we must
                    // drop rather than submit an invalid one.
                    if (!fieldOptions.length) {
                      console.warn(`Prefill skipped for ${field.internal_name}: authoritative options (${field.options_source}) not loaded`);
                    }
                    return;
                  }

                  // Convert date strings to moment objects for date fields
                  if (field.component === 'date') {
                    initialValues[field.internal_name] = parseDateFieldValue(value, PREFILL_DATE_FORMATS);
                  } else if (field.input_mask === 'phone') {
                    initialValues[field.internal_name] = normalizePhoneDigits(value);
                  } else if (authoritative) {
                    initialValues[field.internal_name] = authoritativeValue;
                  } else {
                    initialValues[field.internal_name] = value;
                  }
                } else if (field.options_source) {
                  const fieldOptions = allOptions[field.options_source] || [];
                  if (field.auto_select_single && fieldOptions.length === 1) {
                    // Auto-select if only one option available
                    initialValues[field.internal_name] = fieldOptions[0].value;
                  } else {
                    // Pre-select option marked as default by the backend
                    const defaultOption = fieldOptions.find(opt => opt.metadata?.is_default);
                    if (defaultOption) {
                      initialValues[field.internal_name] = defaultOption.value;
                    }
                  }
                } else if (field.default_value !== undefined) {
                  initialValues[field.internal_name] = field.default_value;
                }
              });
            });
          });
        });
        form.setFieldsValue(initialValues);
        setFormValues(initialValues);
        const taskFieldNames = taskFieldNamesForSections(schemaResponse.sections || []);
        savedTaskValuesRef.current = heldForm?.task_included
          ? Object.fromEntries(Object.entries(initialValues).filter(([name]) => taskFieldNames.has(name)))
          : null;
        // Only now: outside a React event these render at once, and the template
        // restore they start reads the task values recorded above.
        setSavedForm(heldForm);
        if (heldForm?.task_included) setTaskRequested(true);

        acceptSchemaPrefill({ schema: schemaResponse, formValues: initialValues, credentialId });
      } catch (error) {
        if (cancelled) return;

        console.error(error);
        message.error('Unable to load EMR schema for this integration.');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSchema();
    return () => {
      cancelled = true;
    };
  }, [
    acceptSchemaPrefill,
    form,
    onClose,
    parentType,
    prefill,
    prepareForSchemaLoad,
    selectedCredential,
    sendToken,
    visible,
    workflow,
  ]);

  // Stable provider hint strings to avoid re-triggering the match effect
  const orderingName = providerHints?.orderingProviderName;
  const referringName = providerHints?.referringProviderName;

  const handleDynamicOptionsChange = useCallback(async (changedValues) => {
    if (!schema) return;

    const allFields = [];
    (schema.sections || []).forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          allFields.push(...row);
        });
      });
    });

    const dynamicFields = allFields.filter(f => f.dynamic_options_source);

    for (const field of dynamicFields) {
      const { depends_on, mapping } = field.dynamic_options_source;
      if (!(depends_on in changedValues)) continue;

      const selectedValue = changedValues[depends_on];

      // Clear the dependent field value when its dependency changes
      applyFieldValues({ [field.internal_name]: undefined });

      if (!selectedValue || !mapping[selectedValue]) {
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: [] }));
        setDynamicLoading(prev => ({ ...prev, [field.internal_name]: false }));
        continue;
      }

      const source = mapping[selectedValue];

      // Use cached options if already fetched (length check avoids caching empty results permanently)
      if (options[source]?.length) {
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: options[source] }));
        continue;
      }

      // Fetch from API — capture the dependency value to detect stale responses
      const expectedValue = selectedValue;
      setDynamicLoading(prev => ({ ...prev, [field.internal_name]: true }));
      try {
        const res = await axios.get('/api/v1/emr_options', {
          params: {
            emr_integration_credential_id: selectedCredential.id,
            source,
          },
        });

        // Guard against stale responses: only apply if the dependency hasn't changed
        const currentValue = form.getFieldValue(depends_on);
        if (currentValue !== expectedValue) return;

        const fetched = res.data.options || [];
        setOptions(prev => ({ ...prev, [source]: fetched }));
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: fetched }));
      } catch (err) {
        // Ignore errors from stale requests
        const currentValue = form.getFieldValue(depends_on);
        if (currentValue !== expectedValue) return;

        console.error(`Failed to fetch dynamic options for ${source}:`, err);
        setDynamicOptions(prev => ({ ...prev, [field.internal_name]: [] }));
        const apiDetail = err?.response?.data?.error || err?.response?.data?.detail;
        const errorMsg = apiDetail || `Failed to load ${field.label || 'options'}. This feature may not be enabled in your EMR.`;
        message.warning(errorMsg);
        form.setFields([{ name: field.internal_name, errors: [errorMsg] }]);
      } finally {
        const currentValue = form.getFieldValue(depends_on);
        if (currentValue !== expectedValue) return;
        setDynamicLoading(prev => ({ ...prev, [field.internal_name]: false }));
      }
    }
  }, [schema, form, options, selectedCredential]);

  // Clears a suggestion field's value when the field its suggestions depend on
  // changes, so a title picked for the previous category doesn't silently carry
  // over once a new category is selected (mirrors the dependent-dropdown
  // clearing above). The user can still retype it — the field accepts free text.
  // Only values taken from the suggestion source are cleared, and only when the
  // newly selected category doesn't also suggest them. Free text is the user's
  // own input and survives category changes as it always has — including when
  // the feature is off or no category involved has configured options.
  const clearSuggestedFieldsOnDependencyChange = useCallback((changedValues) => {
    if (!schema) return;

    (schema.sections || []).forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          row.forEach((field) => {
            const config = field.suggested_options;
            if (!config || !(config.depends_on in changedValues)) return;

            const currentValue = form.getFieldValue(field.internal_name);
            if (!currentValue) return;

            const sourceOptions = options[config.options_source] || [];
            if (!matchingOptionValue(sourceOptions, currentValue)) return;

            const newCategoryOptions = suggestedOptionsFor(field, options, changedValues[config.depends_on]) || [];
            if (matchingOptionValue(newCategoryOptions, currentValue)) return;

            applyFieldValues({ [field.internal_name]: undefined });
          });
        });
      });
    });
  }, [schema, form, options, applyFieldValues]);

  const handleValuesChange = useCallback((changedValues, allValues) => {
    setFormValues(allValues || form.getFieldsValue(true));
    handleDynamicOptionsChange(changedValues);
    clearSuggestedFieldsOnDependencyChange(changedValues);
    clearForMappedFormChange(changedValues);

    const athenaEncounterContextChanged = schema?.emr?.toString().toLowerCase() === 'athena'
      && (
        Object.prototype.hasOwnProperty.call(changedValues, 'document_category')
        || Object.prototype.hasOwnProperty.call(changedValues, 'location')
      );
    if (athenaEncounterContextChanged) {
      applyFieldValues({ custom_fields: undefined });
    }
    if (changedValues.document_category === ATHENA_PROGRESS_NOTE_CATEGORY) {
      setTaskRequested(false);
      setReviewRequested(false);
    }

    if (!crossFieldErrors.length) return;

    const changedKeys = Object.keys(changedValues);
    const shouldClear = crossFieldErrors.some(
      (err) => err.fields.some((f) => changedKeys.includes(f)),
    );

    if (shouldClear) {
      setCrossFieldErrors([]);
      const fieldsToClear = crossFieldErrors.flatMap(
        (err) => err.fields.map((field) => ({ name: field, errors: [] })),
      );
      form.setFields(fieldsToClear);
    }
  }, [
    applyFieldValues,
    clearSuggestedFieldsOnDependencyChange,
    crossFieldErrors,
    form,
    handleDynamicOptionsChange,
    schema,
    clearForMappedFormChange,
  ]);

  const handleClose = () => {
    recordPatientSearchAbandonment();
    onClose();
  };

  const clearAthenaProgressNoteEncounter = useCallback(() => {
    setAthenaEncounterProof(undefined);
    if (isAthenaProgressNote) {
      applyFieldValues({ custom_fields: undefined });
    }
  }, [applyFieldValues, isAthenaProgressNote]);

  const reloadExpiredAthenaEncounter = (result) => {
    if (!isAthenaProgressNote || !responseHasErrorCode(result, 'invalid_proof')) return;

    clearAthenaProgressNoteEncounter();
    setAthenaEncounterReloadKey((current) => current + 1);
  };

  // The upload payload for the form's values: normalized fields, the patient's
  // ids in place of what the form displays, and the task (and its template)
  // only when one is included. Shared by submitting and by setting the document
  // aside as Awaiting Provider, which keeps the same payload (ENG-3934).
  const buildChartPayload = (values) => {
    // Extract all fields from nested structure for normalization
    const allFields = [];
    (schema.sections || []).forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          allFields.push(...row);
        });
      });
    });

    const payload = mergeMappedPatientSearchValues(
      normalizeFormValues(allFields, values),
      values,
      patientSearchConfig?.field_mappings,
    );
    const taskFieldNames = taskFieldNamesForSections(schema.sections || []);
    const submitWithTask = TASK_EMBEDDING_WORKFLOWS.includes(workflow)
      && !isAthenaProgressNote
      && taskRequested
      && !nextgenTaskTemplates.taskSkipped
      && taskFieldNames.size > 0;

    if (TASK_EMBEDDING_WORKFLOWS.includes(workflow) && !submitWithTask) {
      taskFieldNames.forEach((fieldName) => {
        delete payload[fieldName];
      });
    }

    // The server turns the template's answers into the task description.
    if ((submitWithTask || workflow === 'task_creation') && nextgenTaskTemplates.template) {
      payload.nextgen_task_template = templateSubmission(nextgenTaskTemplates.template, nextgenTaskTemplates.answers);
    }

    if (submitWithTask && reviewRequested && schema.emr === 'Veradigm' && schema.metadata?.needs_review_enabled === true) {
      payload.task_type = 'Review';
      payload.task_associate_document = true;
    }

    if (isAthenaProgressNote) {
      ATHENA_PROGRESS_NOTE_HIDDEN_FIELDS.forEach((fieldName) => delete payload[fieldName]);
    }

    Object.entries(patientIdentifierOverridesRef.current || {}).forEach(([fieldName, meta]) => {
      if (!meta) {
        return;
      }

      const { id, display } = meta;
      if (id === undefined || id === null) {
        return;
      }

      const submittedValueExists = Object.prototype.hasOwnProperty.call(values, fieldName);
      if (display !== undefined && display !== null && submittedValueExists) {
        const currentValue = values[fieldName];
        if (currentValue === undefined || currentValue === null) {
          return;
        }

        if (String(currentValue) !== String(display)) {
          return;
        }
      }

      payload[fieldName] = id;
    });

    return { payload, submitWithTask };
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      if (!schema || !selectedCredential) return;

      // Cross-field validation
      const crossErrors = validateCrossFieldRules(
        schema.metadata?.validations,
        values,
      );
      if (crossErrors.length) {
        setCrossFieldErrors(crossErrors);
        const fieldErrors = crossErrors.flatMap(
          (err) => err.fields.map((field) => ({ name: field, errors: [err.message] })),
        );
        form.setFields(fieldErrors);
        return;
      }

      const { payload, submitWithTask } = buildChartPayload(values);

      const patientIdentifierOverride = patientIdentifierOverridesRef.current?.patient_identifier;
      const selectedPatientIdentifierSubmitted = selectedPatient
        && hasPresentValue(patientIdentifierOverride?.id)
        && String(payload.patient_identifier) === String(patientIdentifierOverride.id);

      if (requiresPatientResultSelection && !selectedPatientIdentifierSubmitted) {
        captureAnalytics('emr_patient_search_submission_blocked', {
          credential_id: selectedCredential.id,
          integration_type: schema?.emr,
          reason: 'selection_required',
        });
        message.error('Select a patient from search results before submitting.');
        return;
      }

      // Map workflow to upload_type
      const workflowToUploadType = {
        create_chart: submitWithTask ? 'chart_and_task' : 'chart_creation',
        send_to_chart: submitWithTask ? 'upload_and_task' : 'auto_upload',
        task_creation: 'task_creation',
      };
      const uploadType = workflowToUploadType[workflow] || 'auto_upload';

      // Clear any existing messages before making the request
      message.destroy();

      const chartUploadRequest = {
        emrIntegrationCredentialId: selectedCredential.id,
        athenaEncounterProof: isAthenaProgressNote ? athenaEncounterProof : undefined,
        data: payload,
        uploadType,
        parentType,
        sendToken,
        idempotencyKey: getSubmissionIdempotencyKey(),
        ...(archiveAfterUpload && !submitWithTask && { archiveAfterUpload: true }),
        ...(schema?.emr === 'NextGen' && {
          patientSelectionProof: selectedPatient?.selection_proof,
        }),
      };

      const result = await createChartUpload(chartUploadRequest);

      // Check if the action returned an error
      if (result && result.error) {
        resetKeyAfterConfirmedRejection(result);
        reloadExpiredAthenaEncounter(result);
        // Prefer the server's own copy. requestTransport only promotes a derived
        // message into top-level `message` when the body carried `data.errors`
        // (plural array); the reply chart endpoints answer with `{error: "..."}`
        // (singular), so `result.message` would stay as axios' generic
        // "Request failed with status code 422" and the actionable ENG-3012 text
        // ("Reply has no uploadable document", "TIFF reply attachments are not
        // currently supported for chart upload.", "A document could not be
        // prepared for chart upload.") would never reach the user.
        const errorMessage = result.response?.data?.error
          || result.message
          || 'Unable to queue chart upload.';
        message.error(errorMessage);
        return;
      }

      recordPatientSearchSubmission();

      // ENG-3012 TIFF deferral: partial success (visible TIFFs excluded from a
      // reply filing) must not disappear behind the ordinary success toast --
      // the drawer closes only once the warning is acknowledged.
      const tiffWarnings = extractReplyTiffWarnings(result);
      if (tiffWarnings.length > 0) {
        // The drawer stays open until the warning is acknowledged, so the
        // modal (antd default 1000) has to sit above it.
        showReplyTiffWarningModal({
          warnings: tiffWarnings, workflow, onAcknowledge: handleClose, zIndex: DRAWER_Z_INDEX + 1,
        });
        return;
      }

      const queuedMessage = workflow === 'task_creation'
        ? 'Task queued. We will notify you of any issues.'
        : archiveAfterUpload
          ? 'Chart upload queued. The record will archive after successful filing if its status still allows it.'
          : 'Chart upload queued. We will notify you of any issues.';
      message.success(queuedMessage);
      handleClose();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      const errorMessage = normalizeRequestError(error).response.data.error;
      resetKeyAfterConfirmedRejection(error);
      reloadExpiredAthenaEncounter(error);
      message.error(errorMessage);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const fieldDefinitions = useMemo(() => {
    const map = {};

    // Extract all fields from nested sections/subsections/rows
    (schema?.sections || []).forEach(section => {
      (section.subsections || []).forEach(subsection => {
        (subsection.rows || []).forEach(row => {
          row.forEach(field => {
            map[field.internal_name] = field;
          });
        });
      });
    });

    return map;
  }, [schema]);

  // ENG-3934: the form an Awaiting Provider hold keeps, so the document can be
  // finished from it once the provider is in NextGen: the upload payload as it
  // stands, with the NextGen document as filled in so far (all but the
  // referring provider). The server checks it is otherwise complete.
  const buildAwaitingProviderForm = () => {
    if (!schema || !selectedCredential) return null;

    // The mirror the required-field checks read, so what's saved is what was checked.
    const { payload, submitWithTask } = buildChartPayload(formValues);
    Object.values(fieldDefinitions).forEach((field) => {
      if (field.component === NEXTGEN_METADATA_COMPONENT) payload[field.internal_name] = nextgenHoldDraft;
    });
    // The answers as shown (with their display text), so the template can be
    // restored as it was.
    if (payload.nextgen_task_template) payload.nextgen_task_template.answers = nextgenTaskTemplates.answers;
    return {
      credential_id: selectedCredential.id,
      workflow,
      task_included: submitWithTask,
      values: payload,
    };
  };
  buildAwaitingProviderFormRef.current = buildAwaitingProviderForm;


  // Fields with options_scope: location (e.g. NextGen workgroups, which differ per
  // practice) load their options for the selected location whenever it changes,
  // including the first auto-selected or prefilled value that onValuesChange never
  // reports. A changed location clears their values: an id picked for one
  // practice means nothing in another.
  const selectedLocation = formValues.location;
  useEffect(() => {
    const scopedFields = Object.values(fieldDefinitions)
      .filter((field) => field.options_scope === 'location' && field.options_source);
    if (!schema || !selectedCredential?.id || !scopedFields.length) return undefined;

    const previous = locationScopedRef.current;
    if (previous.schema === schema && previous.location !== selectedLocation) {
      applyFieldValues(Object.fromEntries(scopedFields.map((field) => [field.internal_name, undefined])));
    }
    locationScopedRef.current = { schema, location: selectedLocation };
    setLocationScopedOptions({});
    if (!selectedLocation) return undefined;

    let cancelled = false;
    scopedFields.forEach(async (field) => {
      try {
        const res = await axios.get('/api/v1/emr_options', {
          params: {
            emr_integration_credential_id: selectedCredential.id,
            source: field.options_source,
            location: selectedLocation,
          },
        });
        if (cancelled) return;
        setLocationScopedOptions((prev) => ({ ...prev, [field.internal_name]: res.data.options || [] }));
      } catch (err) {
        if (cancelled) return;
        const errorMsg = err?.response?.data?.error || `Failed to load ${field.label || 'options'}.`;
        form.setFields([{ name: field.internal_name, errors: [errorMsg] }]);
      }
    });
    return () => { cancelled = true; };
  }, [schema, fieldDefinitions, selectedCredential?.id, selectedLocation, applyFieldValues, form]);

  // Requests are offered only beside a credential-scoped provider search (a
  // schema field, or a NextGen document type's referring provider field), and
  // only when the credential has them configured (otherwise /new is a 404).
  const hasCredentialScopedProviderField = useMemo(
    () => nextgenReferringField || Object.values(fieldDefinitions).some(
      (field) => field.component === 'emr_provider_autocomplete' && field.credential_scoped,
    ),
    [fieldDefinitions, nextgenReferringField],
  );
  const providerRequestCredentialId = selectedCredential?.id;

  useEffect(() => {
    setProviderRequestConfig(null);
    setProviderRequestSearch(null);
    setProviderRequestSent(false);
    setAwaitingProviderHold(null);
    setAwaitingProviderFailed(false);
    if (!visible || !providerRequestCredentialId || !hasCredentialScopedProviderField) return undefined;

    let cancelled = false;
    axios.get('/api/v1/referring_provider_requests/new', {
      params: {
        emr_integration_credential_id: providerRequestCredentialId,
        parent_type: parentType,
        send_token: sendToken,
      },
    }).then((response) => {
      if (cancelled) return;
      setProviderRequestConfig(response.data);
      // A mark that landed after this was read is newer than what it says.
      setAwaitingProviderHold((placed) => response.data?.awaiting_provider || placed);
    }).catch(() => {
      // 404: not configured for this credential. Anything else: the search
      // still works; only the request link is missing.
    });
    return () => { cancelled = true; };
  }, [visible, providerRequestCredentialId, hasCredentialScopedProviderField, parentType, sendToken]);

  const openProviderRequest = useCallback((searchText) => {
    captureAnalytics('referring_provider_request_opened', { workflow, from_search: Boolean(searchText) });
    setProviderRequestSearch(searchText || '');
  }, [workflow]);

  // Offered beside the same credential-scoped provider search as requests, on a
  // received fax, email attachment or receiver that isn't already held. A loaded request config
  // means this credential is NextGen with requests configured, which is exactly
  // when the organization has Awaiting Provider.
  const canMarkAwaitingProvider = Boolean(providerRequestConfig)
    && canHoldDocument(parentType, sendToken, { sentContext: isSentContext })
    && !awaitingProviderHold;

  const awaitingProviderDocument = `${parentType}:${sendToken}`;
  useEffect(() => {
    awaitingProviderDocumentRef.current = awaitingProviderDocument;
  }, [awaitingProviderDocument]);

  // Also sent for a document that is already held, once a provider request
  // goes out, so the hold records the requested provider (ENG-3934).
  const markAwaitingProvider = useCallback(async (source, requestedProvider = null) => {
    const markedDocument = awaitingProviderDocument;
    const marking = markingAwaitingProviderRef.current;
    if (marking.has(markedDocument)) return;
    marking.add(markedDocument);
    setAwaitingProviderFailed(false);
    try {
      const hold = await placeAwaitingProviderHold({
        parentType, sendToken, form: buildAwaitingProviderFormRef.current?.(), requestedProvider,
      });
      captureAnalytics('awaiting_provider_marked', { source, workflow });
      refreshInbox('awaiting_provider_marked');
      if (awaitingProviderDocumentRef.current === markedDocument) setAwaitingProviderHold(hold || {});
    } catch {
      if (awaitingProviderDocumentRef.current === markedDocument) setAwaitingProviderFailed(true);
    } finally {
      marking.delete(markedDocument);
    }
  }, [awaitingProviderDocument, parentType, sendToken, workflow, refreshInbox]);

  // Under the provider search rather than a toast, which the drawer would cover.
  const providerNotes = () => {
    if (!providerRequestSent && !awaitingProviderHold && !awaitingProviderFailed) return null;

    return (
      <div className="dynamic-ehr-drawer__provider-notes">
        {providerRequestSent && (
          <span className="dynamic-ehr-drawer__provider-request-sent" role="status">
            Request sent. A copy was emailed to you. Once the practice adds the provider, it can be
            picked here (usually the next day).
          </span>
        )}
        {awaitingProviderHold && (
          <span className="dynamic-ehr-drawer__awaiting-provider" role="status">
            <strong>Awaiting Provider.</strong>
            {` ${describeHold(awaitingProviderHold)} Find it with the Awaiting Provider filter; it drops off once this document is filed.`}
          </span>
        )}
        {awaitingProviderFailed && (
          <span className="dynamic-ehr-drawer__awaiting-provider dynamic-ehr-drawer__awaiting-provider--error" role="alert">
            This document could not be marked Awaiting Provider. Try Mark Awaiting Provider in the provider search.
          </span>
        )}
      </div>
    );
  };

  // Auto-match AI-extracted provider names to an EmrProvider and pre-populate the field
  useEffect(() => {
    if (!visible || loading || !schema) return;
    if (!orderingName && !referringName) return;

    const hasProviderField = Object.values(fieldDefinitions).some(
      (f) => f.internal_name === 'provider_id' && f.component === 'emr_provider_autocomplete'
    );
    if (!hasProviderField) return;

    const currentValue = form.getFieldValue('provider_id');
    if (currentValue) return;

    let cancelled = false;
    const controller = new AbortController();

    const matchProvider = async () => {
      try {
        const response = await axios.post('/api/v1/emr_providers/match', {
          ordering_provider_name: orderingName,
          referring_provider_name: referringName,
        }, {
          signal: controller.signal,
          headers: {
            'X-CSRF-Token': document.getElementsByName('csrf-token')[0]?.content,
          },
        });

        const { matched, provider } = response.data;
        if (cancelled) return;

        // Re-check before setting — user may have selected a provider while request was in-flight
        if (matched && provider?.id && !form.getFieldValue('provider_id')) {
          applyFieldValues({ provider_id: provider.id });
          setFlashingFields((prev) => ({ ...prev, provider_id: true }));
          setTimeout(() => {
            setFlashingFields((prev) => {
              const next = { ...prev };
              delete next.provider_id;
              return next;
            });
          }, 700);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.warn('Provider matching failed:', error);
        }
      }
    };

    matchProvider();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [visible, loading, schema, orderingName, referringName, fieldDefinitions, form]);

  const getFieldOptions = useCallback((field) => {
    // Check for dynamically-loaded options (e.g., delegate_id depends on delegate_type)
    if (field.dynamic_options_source && dynamicOptions[field.internal_name]) {
      return dynamicOptions[field.internal_name];
    }
    if (field.options_scope === 'location') {
      return locationScopedOptions[field.internal_name] || [];
    }

    const inlineOptions = field.options || [];
    const sourceOptions = field.options_source ? (options[field.options_source] || []) : [];

    if (inlineOptions.length) {
      return inlineOptions;
    }

    // Authoritative-source fields (options_strict, e.g. Veradigm Unity
    // dictionary entries) are never Medsender organization categories: always
    // use the fetched source options (even when empty) so they never fall
    // through to the Redux category mapping below.
    if (usesAuthoritativeOptions(field)) {
      return sourceOptions;
    }

    if (field.options_source && sourceOptions.length) {
      return sourceOptions;
    }

    // Prefer Redux state for legacy document-category schemas, but fall
    // through to fetched source options when the store is empty (failed or
    // pending fetch, org without categories) so a required select never
    // renders empty while valid options exist.
    if (field.internal_name === 'document_category') {
      const mapped = dedupeDocumentCategories(documentCategories)
        .map((category) => ({
          value: category.emr_category,
          label: category.emr_category,
        }));
      if (mapped.length) return mapped;
    }

    return sourceOptions;
  }, [documentCategories, options, dynamicOptions, locationScopedOptions]);

  // Some schema-driven selects can describe an automatic default derived from
  // another field. The rule map is supplied as metadata on the field's existing
  // options source; keeping this declarative avoids coupling the drawer to a
  // particular EMR or routing implementation.
  const autoAssignPlaceholderFor = useCallback((field) => {
    const config = field.auto_assign_placeholder;
    if (!config) return undefined;

    const category = formValues[config.category_field];
    if (category === undefined || category === null || category === '') return undefined;

    const rules = optionMetadata[config.options_source]?.[config.response_key];
    const rule = rules?.[category];
    const assignees = Array.isArray(rule?.assignees) ? rule.assignees.filter(Boolean) : [];
    if (!rule || assignees.length === 0) return undefined;

    return rule.mode === 'round_robin'
      ? 'Auto-assign (round-robin)'
      : `Auto-assign (${assignees[0]})`;
  }, [formValues, optionMetadata]);

  const renderFieldLabel = (field) => {
    if (!field.label) {
      return null;
    }

    const helpText = field.help_text?.trim();
    if (helpText) {
      return (
        <span className="dynamic-ehr-drawer__field-label">
          {field.label}
          {' '}
          <Tooltip title={helpText} getPopupContainer={() => document.body} overlayClassName="ehr-drawer-tooltip">
            <span className="dynamic-ehr-drawer__help-icon">&#9432;</span>
          </Tooltip>
        </span>
      );
    }

    return (
      <span className="dynamic-ehr-drawer__field-label">
        {field.label}
      </span>
    );
  };

  // `slot` is the field this one adopted, if the schema declared a pairing. Only
  // a component that knows what to do with it reads it.
  // NextGen caps the task description at 1000 characters, template lines included.
  const renderTemplateNotesCounter = () => {
    const left = nextgenTaskTemplates.charactersLeft;
    if (left >= 0) return `${left} characters left, counting the template's lines`;
    return (
      <span style={{ color: '#cf1322' }}>
        {`${-left} characters over NextGen's 1,000-character limit, counting the template's lines`}
      </span>
    );
  };

  const renderField = (field, { slot } = {}) => {
    const {
      internal_name: name,
      label,
      component,
      required,
      help_text: helpText,
      width,
      validation,
    } = field;

    if (component === ATHENA_ENCOUNTER_COMPONENT && !isAthenaProgressNote) {
      return null;
    }

    if (isAthenaProgressNote && ATHENA_PROGRESS_NOTE_HIDDEN_FIELDS.has(name)) {
      return null;
    }

    if (isHiddenField(field)) {
      return (
        <Form.Item key={name} name={name} noStyle preserve={false}>
          <Input type="hidden" />
        </Form.Item>
      );
    }

    const fieldOptions = getFieldOptions(field);

    // `hide_when_auto_selected`: once the option list has collapsed to a single
    // choice the prefill pass already made, showing a one-entry dropdown asks
    // the user to re-make a decision they have no say in. A property of the
    // field (set in the schema), not of any one EMR.
    //
    // The held value must be that sole option. A prefill that named something
    // else — a stale fax-derived location, an option the source has since
    // dropped — is a value the user still needs to see and correct, so the field
    // stays visible rather than hiding a mismatch behind a hidden input.
    if (
      field.hide_when_auto_selected
      && fieldOptions.length === 1
      && hasPresentValue(formValues[name])
      && formValues[name] === fieldOptions[0].value
    ) {
      return (
        <Form.Item key={name} name={name} noStyle preserve>
          <Input type="hidden" />
        </Form.Item>
      );
    }

    const fieldClassNames = ['dynamic-ehr-drawer__field'];

    // Add width-based CSS class
    const widthClass = getFieldWidthClass(width);
    fieldClassNames.push(widthClass);

    // Add flash class if field was just updated
    if (flashingFields[name]) {
      fieldClassNames.push('field-flash');
    }

    // The NextGen practice selector is fed by a filtered catalog: a practice
    // only appears once it can actually serve a document type. Zero options is
    // therefore a configuration state with a known remedy, not a transient
    // empty list (the drawer renders a spinner, not fields, while options load),
    // so the remediation replaces the dropdown. The field keeps its form binding
    // so a required practice still blocks submission.
    if (
      schema?.emr === 'NextGen'
      && component === 'select'
      && name === 'location'
      && fieldOptions.length === 0
    ) {
      return (
        <div key={name} className={fieldClassNames.join(' ')} style={{ flex: getFieldFlexStyle(width) }}>
          <Form.Item
            name={name}
            noStyle
            preserve={false}
            rules={required ? [{ required: true, message: `${label || name} is required` }] : undefined}
          >
            <Input type="hidden" />
          </Form.Item>
          <Alert type="warning" showIcon message={NEXTGEN_PRACTICE_EMPTY_STATE} />
        </div>
      );
    }

    let inputNode;
    // Form.Item props a component needs beyond the defaults. Set by the case
    // that needs them so a field type's markup and its binding stay together,
    // rather than accreting `component === 'x'` ternaries on the Form.Item.
    let itemProps = {};
    switch (component) {
      case 'textarea':
        inputNode = <TextArea rows={3} />;
        break;
      // Renders the label beside the box rather than above it, so a boolean
      // flag reads as one line instead of an empty-looking labelled field.
      // The help icon rides along inside the box's own label, because the
      // Form.Item label — the usual home for help_text — is suppressed here.
      case 'checkbox':
        itemProps = {
          label: null,
          valuePropName: 'checked',
          // Redundant with valuePropName under antd 4 — rc-field-form already
          // reads target[valuePropName] — but stated explicitly so the boolean
          // contract does not depend on that default surviving an antd upgrade.
          getValueFromEvent: (e) => !!e?.target?.checked,
        };
        inputNode = (
          <Checkbox
            onChange={(e) => captureAnalytics('ehr_drawer_checkbox_toggled', {
              field: name,
              emr: schema?.emr,
              workflow,
              checked: !!e?.target?.checked,
            })}
          >
            {label}
            {helpText?.trim() && (
              <>
                {' '}
                {renderCheckboxHelpIcon(helpText.trim())}
              </>
            )}
          </Checkbox>
        );
        break;
      case 'date':
        inputNode = (
          <DatePicker
            style={{ width: '100%' }}
            format="MM/DD/YYYY"
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          />
        );
        break;
      case 'select': {
        const isDynamicDependent = !!field.dynamic_options_source;
        const dependencyValue = isDynamicDependent
          ? form.getFieldValue(field.dynamic_options_source.depends_on)
          : true;
        const isDisabled = isDynamicDependent && !dependencyValue;
        const isLoadingDynamic = isDynamicDependent && dynamicLoading[name];
        const autoAssignPlaceholder = autoAssignPlaceholderFor(field);
        const placeholder = autoAssignPlaceholder || field.placeholder;

        inputNode = (
          <Select
            showSearch
            style={{ width: '100%' }}
            optionFilterProp="children"
            optionLabelProp="children"
            allowClear
            loading={isLoadingDynamic}
            disabled={isDisabled || isLoadingDynamic}
            placeholder={isLoadingDynamic ? 'Loading options...' : (isDisabled ? (placeholder || 'Select...') : placeholder)}
            value={form.getFieldValue(name)}
            onChange={(val) => {
              const nextgenPracticeChanged = schema?.emr === 'NextGen' && name === 'location';
              if (nextgenPracticeChanged) {
                patientSearchResetRef.current?.();
              }
              applyFieldValues({ [name]: val });
              if (nextgenPracticeChanged) {
                captureAnalytics('nextgen_practice_selected', { workflow });
              }
              if (field.options_source === 'veradigm/caregivers') {
                captureAnalytics('veradigm_review_caregiver_selected', { workflow, review_requested: reviewRequested, selected: !!val });
              }
            }}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          >
            {fieldOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        );
        break;
      }
      case 'multi_select':
        inputNode = (
          <Select
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            optionFilterProp="children"
            optionLabelProp="children"
            allowClear
            value={form.getFieldValue(name)}
            onChange={(vals) => applyFieldValues({ [name]: vals })}
            getPopupContainer={() => document.body}
            dropdownClassName="ehr-select-dropdown"
          >
            {renderGroupedOptions(fieldOptions)}
          </Select>
        );
        break;
      case 'emr_provider_autocomplete':
        inputNode = (
          <EmrProviderAutocomplete
            value={form.getFieldValue(name)}
            onChange={(val) => applyFieldValues({ [name]: val })}
            placeholder={field.placeholder || 'Search providers...'}
            credentialId={field.credential_scoped ? selectedCredential?.id : null}
            onAnalytics={(event, properties) => captureAnalytics(event, { ...properties, workflow })}
            onRequestProvider={field.credential_scoped && providerRequestConfig ? openProviderRequest : null}
            onMarkAwaitingProvider={
              field.credential_scoped && canMarkAwaitingProvider ? () => markAwaitingProvider('search_footer') : null
            }
            actionsBlockedReason={field.credential_scoped ? providerActionsBlockedReason : null}
          />
        );
        if (field.credential_scoped && providerNotes()) itemProps = { extra: providerNotes() };
        break;
      case NEXTGEN_METADATA_COMPONENT:
        inputNode = (
          <NextgenDocumentMetadataFields
            credentialId={selectedCredential?.id}
            practiceId={formValues.location}
            dateOfServicePrefill={prefill.date_of_service}
            documentCategoryId={schema?.prefill?.nextgen_document_category_id}
            documentTitle={slot}
            onAnalytics={captureAnalytics}
            onChange={(value) => applyFieldValues({ [name]: value })}
            onDocumentTypeChange={nextgenTaskTemplates.setDocumentTypeId}
            onReferringProviderField={setNextgenReferringField}
            onRequestProvider={providerRequestConfig ? openProviderRequest : null}
            onMarkAwaitingProvider={canMarkAwaitingProvider ? () => markAwaitingProvider('search_footer') : null}
            onDraftChange={setNextgenHoldDraft}
            savedDocument={savedForm?.values?.[name]?.nextgen_document || null}
            providerActionsBlockedReason={providerActionsBlockedReason}
            referringProviderNotes={providerNotes()}
          />
        );
        break;
      case ATHENA_ENCOUNTER_COMPONENT:
        itemProps = { label: null };
        inputNode = (
          <AthenaEncounterSelector
            active={isAthenaProgressNote}
            credentialId={selectedCredential?.id}
            patientId={selectedPatient?.value}
            departmentId={formValues.location}
            onAnalytics={captureAnalytics}
            onChange={(value) => applyFieldValues({ [name]: value })}
            onProofChange={setAthenaEncounterProof}
            reloadKey={athenaEncounterReloadKey}
          />
        );
        break;
      case ATHENA_INSURANCE_COMPONENT:
        inputNode = (
          <AthenaInsuranceSection
            credentialId={selectedCredential?.id}
            departmentId={formValues.location}
            patientDefaults={{
              firstName: formValues.patient_first_name,
              lastName: formValues.patient_last_name,
              dob: formValues.patient_dob,
              sex: formValues.patient_gender,
            }}
            prefill={{
              primary_plan: prefill.insurance_primary_payer,
              primary_member_id: prefill.insurance_primary_member_id,
              insurance_group_number: prefill.insurance_group_number,
              insurance_effective_date: prefill.insurance_effective_date,
              secondary_plan: prefill.insurance_secondary_payer,
              secondary_member_id: prefill.insurance_secondary_member_id,
              secondary_group_number: prefill.insurance_secondary_group_number,
            }}
            onAnalytics={captureAnalytics}
            onChange={(value) => applyFieldValues({ [name]: value })}
            onValidationChange={(validationError) => {
              setInsuranceValidationError(validationError);
              if (!validationError) form.setFields([{ name, errors: [] }]);
            }}
          />
        );
        break;
      default: {
        if (!KNOWN_FREE_TEXT_COMPONENTS.includes(component)) {
          // The warning is emitted once per schema load, not here: this branch
          // re-runs on every keystroke that re-renders the drawer.
          return (
            <Alert
              key={name}
              type="error"
              showIcon
              className={fieldClassNames.join(' ')}
              message={`This integration field cannot be displayed (${component}).`}
            />
          );
        }

        // Suggestion fields (e.g. document_title suggesting the titles
        // configured for the selected document_category): render an
        // autocomplete offering the suggestions while still accepting free
        // text, otherwise fall through to a plain input.
        const suggestedOptions = field.suggested_options
          ? suggestedOptionsFor(
            field,
            options,
            form.getFieldValue(field.suggested_options.depends_on),
          )
          : null;

        if (suggestedOptions && suggestedOptions.length) {
          inputNode = (
            <AutoComplete
              style={{ width: '100%' }}
              allowClear
              placeholder={field.placeholder}
              value={form.getFieldValue(name)}
              onChange={(val) => applyFieldValues({ [name]: val })}
              options={suggestedOptions.map((option) => ({ value: option.value, label: option.label }))}
              filterOption={(inputValue, option) => (
                String(option.value).toLowerCase().includes(String(inputValue).toLowerCase())
              )}
              getPopupContainer={() => document.body}
              dropdownClassName="ehr-select-dropdown"
            />
          );
        } else if (field.input_mask === 'phone') {
          inputNode = <EhrPhoneInput placeholder={field.placeholder} />;
        } else {
          inputNode = <Input placeholder={field.placeholder} />;
        }
        break;
      }
    }

    // Get flex style from width
    const formItemStyle = { flex: getFieldFlexStyle(width) };

    // Build validation rules
    const rules = [];
    if (required) {
      rules.push({ required: true, message: `${label || name} is required` });
    }
    if (validation?.pattern) {
      const patternMessage = validation?.error_message || `${label || name} format is invalid`;
      rules.push({
        pattern: new RegExp(validation.pattern),
        message: patternMessage,
      });
    }
    // With a task template, the description's room depends on the template's
    // lines, so the counter below replaces the fixed maximum.
    const templateCountsNotes = name === 'task_notes' && Boolean(nextgenTaskTemplates.template);
    // With a template, this field is what follows the template's lines.
    const labelField = templateCountsNotes
      ? { ...field, label: 'Additional notes', help_text: "Added below the template's lines in the task description." }
      : field;
    if (validation?.max_length && !templateCountsNotes) {
      rules.push({
        max: validation.max_length,
        message: `${label || name} must be ${validation.max_length} characters or less`,
      });
    }

    // Enable real-time validation for fields with patterns (email, phone, etc.)
    // and for multi-selects, which can be emptied from a tag's remove icon
    // without ever taking focus, so a blur-only required error never appears.
    const validateTrigger = validation?.pattern || component === 'multi_select' ? ['onChange', 'onBlur'] : ['onBlur'];
    const optionsLoadError = taskOptionErrors[field.options_source];

    return (
      <Form.Item
        key={name}
        name={name}
        label={['checkbox', ATHENA_INSURANCE_COMPONENT].includes(component) ? null : (renderFieldLabel(labelField) || label)}
        valuePropName={component === 'checkbox' ? 'checked' : 'value'}
        rules={rules}
        className={fieldClassNames.join(' ')}
        style={formItemStyle}
        colon={false}
        preserve={false}
        validateTrigger={validateTrigger}
        validateStatus={optionsLoadError ? 'error' : undefined}
        help={optionsLoadError}
        extra={templateCountsNotes ? renderTemplateNotesCounter() : undefined}
        // Set by the switch case above; overrides the defaults it names.
        {...itemProps}
      >
        {inputNode}
      </Form.Item>
    );
  };

  // Helper to render a row of fields
  // Everything an owning component needs to render a field it has adopted, as
  // plain data. Deliberately not a React node: where and how the control appears
  // is the owner's business, and the drawer keeps only the form binding.
  const adoptedFieldSlot = (field) => ({
    name: field.internal_name,
    label: renderFieldLabel(field) || field.label,
    placeholder: field.placeholder,
    value: form.getFieldValue(field.internal_name),
    onChange: (value) => applyFieldValues({ [field.internal_name]: value }),
  });

  // An adopted field keeps its value in the form; its owner renders the control
  // the user actually sees.
  const renderAdoptedField = (field) => (
    <Form.Item key={field.internal_name} name={field.internal_name} noStyle preserve>
      <Input type="hidden" />
    </Form.Item>
  );

  const renderFieldRow = (fieldsInRow, key) => {
    if (!fieldsInRow || fieldsInRow.length === 0) return null;

    const rowClass = getRowClass(fieldsInRow);
    // The drawer's whole part in a declared pairing: work out which field owns
    // which, hand the owner the slot, and stay out of the layout it builds.
    const { adoptedByOwner, adoptedNames } = resolveFieldSlots(fieldsInRow);

    return (
      <div className={rowClass} key={key}>
        {fieldsInRow.map((field) => {
          if (adoptedNames.has(field.internal_name)) return renderAdoptedField(field);

          const adopted = adoptedByOwner[field.internal_name];
          return renderField(field, { slot: adopted ? adoptedFieldSlot(adopted) : undefined });
        })}
      </div>
    );
  };

  // Helper to get rows from subsection
  const getSubsectionRows = (subsection) => {
    return subsection.rows || [];
  };

  // Get sections directly from schema (backend sends fully structured data)
  const groupedFields = useMemo(() => {
    if (!schema?.sections) return [];
    return schema.sections;
  }, [schema]);

  const sectionStructure = useMemo(() => {
    return groupedFields.map((section, sectionIndex) => {
      const baseId = `section-${sectionIndex}`;
      const sectionId = section.title
        ? `${baseId}-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
        : baseId;

      const subsections = section.subsections.map((subsection, subsectionIndex) => {
        const subsectionBase = `${sectionId}-subsection-${subsectionIndex}`;
        const subsectionId = subsection.title
          ? `${subsectionBase}-${subsection.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
          : subsectionBase;

        return {
          ...subsection,
          id: subsectionId,
        };
      });

      return {
        ...section,
        id: sectionId,
        subsections,
      };
    });
  }, [groupedFields]);

  const taskFieldNames = useMemo(() => taskFieldNamesForSections(sectionStructure), [sectionStructure]);

  const taskDefaultValues = useMemo(() => {
    const defaults = {};
    taskFieldsForSections(sectionStructure).forEach((field) => {
      if (field.internal_name && field.default_value !== undefined) {
        defaults[field.internal_name] = field.default_value;
      }
    });
    return defaults;
  }, [sectionStructure]);

  const hiddenFields = useMemo(() => {
    const fieldsByName = new Map();
    sectionStructure.forEach((section) => {
      (section.subsections || []).forEach((subsection) => {
        (subsection.rows || []).forEach((row) => {
          (row || []).forEach((field) => {
            const name = field?.internal_name;
            if (name && isHiddenField(field) && !fieldsByName.has(name)) {
              fieldsByName.set(name, field);
            }
          });
        });
      });
    });

    return Array.from(fieldsByName.values());
  }, [sectionStructure]);

  const embeddedTaskAvailable = useMemo(() => (
    TASK_EMBEDDING_WORKFLOWS.includes(workflow)
      && sectionStructure.some((section) => (
        isTaskSchemaEntry(section)
          || (section.subsections || []).some((subsection) => isTaskSchemaEntry(subsection))
      ))
  ), [sectionStructure, workflow]);

  const needsReviewAvailable = schema?.emr === 'Veradigm' && workflow === 'send_to_chart'
    && schema?.metadata?.needs_review_enabled === true && embeddedTaskAvailable;
  const effectiveReviewRequested = needsReviewAvailable && reviewRequested;
  const effectiveTaskRequested = taskRequested && !isAthenaProgressNote;

  const visibleSectionStructure = useMemo(() => {
    return sectionStructure.reduce((visibleSections, section) => {
      const shouldGateTaskSections = embeddedTaskAvailable;
      const sectionIsTask = isTaskSchemaEntry(section);
      if (shouldGateTaskSections && sectionIsTask && !effectiveTaskRequested) {
        return visibleSections;
      }

      // A NextGen template waiting on its trigger field keeps the Task card for
      // its own fields but not the task's (they would block a document-only Submit).
      if (sectionIsTask && nextgenTaskTemplates.taskSkipped) {
        visibleSections.push({ ...section, subsections: [] });
        return visibleSections;
      }

      const subsections = (section.subsections || []).reduce((visibleSubsections, subsection) => {
        if (shouldGateTaskSections && isTaskSchemaEntry(subsection) && !effectiveTaskRequested) {
          return visibleSubsections;
        }

        const rows = visibleRowsForSubsection(subsection)
          .flatMap((row) => {
            const fields = effectiveReviewRequested ? row.filter((field) => field.internal_name !== 'task_type') : row;
            return fields.length > 0 ? [fields] : [];
          });
        if (rows.length === 0) {
          return visibleSubsections;
        }

        visibleSubsections.push({
          ...subsection,
          rows,
        });
        return visibleSubsections;
      }, []);

      if (subsections.length === 0) {
        return visibleSections;
      }

      visibleSections.push({
        ...section,
        subsections,
      });
      return visibleSections;
    }, []);
  }, [effectiveTaskRequested, effectiveReviewRequested, embeddedTaskAvailable, nextgenTaskTemplates.taskSkipped, sectionStructure]);

  const requiredVisibleFields = useMemo(
    () => requiredFieldsForSections(visibleSectionStructure),
    [visibleSectionStructure],
  );

  const missingRequiredVisibleField = useMemo(() => (
    requiredVisibleFields.some(field => !hasPresentValue(formValues[field.internal_name]))
  ), [formValues, requiredVisibleFields]);

  const resolveMappingPath = useCallback((source, rawPath) => {
    if (!rawPath) {
      return undefined;
    }

    const segments = Array.isArray(rawPath) ? rawPath : rawPath.toString().split('.');
    return segments.reduce((acc, key) => {
      if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
        return acc[key];
      }
      return undefined;
    }, source);
  }, []);

  const mapPatientToFields = useCallback((patient) => {
    patientIdentifierOverridesRef.current = {};

    if (!patient) {
      return {};
    }

    const hydratedPatient = {
      ...patient,
      metadata: patient?.metadata || {},
    };

    const mappedFromConfig = Object.entries(patientSearchSettings?.fieldMappings || {}).reduce((acc, [fieldName, path]) => {
      const rawValue = resolveMappingPath(hydratedPatient, path);
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        acc[fieldName] = undefined;
        return acc;
      }

      const fieldDefinition = fieldDefinitions[fieldName];
      let valueToAssign = rawValue;

      if (fieldDefinition?.component === 'date') {
        const momentValue = moment(rawValue, [moment.ISO_8601, 'MM/DD/YYYY'], true);
        valueToAssign = momentValue.isValid() ? momentValue : rawValue;
      }

      if (fieldName === 'patient_identifier') {
        const patientId = rawValue;
        const mrnValue = extractPatientMrn(hydratedPatient);
        const displayValue = derivePatientIdentifierDisplay(patientId, mrnValue);

        valueToAssign = displayValue;

        if (patientIdentifierOverridesRef.current && patientId !== undefined && patientId !== null) {
          patientIdentifierOverridesRef.current[fieldName] = {
            id: patientId,
            display: displayValue,
          };
        }
      }

      acc[fieldName] = valueToAssign;
      return acc;
    }, {});

    return applyPatientFallbacks(patient, mappedFromConfig, fieldDefinitions, patientIdentifierOverridesRef);
  }, [patientSearchSettings, resolveMappingPath, fieldDefinitions, patientIdentifierOverridesRef]);

  const clearSelectedPatientFormFields = useCallback((patient) => {
    const updates = mapPatientToFields(patient);
    const fieldsToClear = clearValuesForFields(updates);
    if (Object.keys(fieldsToClear).length > 0) {
      applyFieldValues(fieldsToClear);
    }
  }, [form, mapPatientToFields]);

  const handlePatientSearchFieldChange = useCallback((key, value) => {
    changePatientSearchField(key, value, clearSelectedPatientFormFields);
    clearAthenaProgressNoteEncounter();
  }, [changePatientSearchField, clearSelectedPatientFormFields, clearAthenaProgressNoteEncounter]);

  const handlePatientSearchReset = useCallback(() => {
    resetPatientSearch(clearSelectedPatientFormFields);
    clearAthenaProgressNoteEncounter();
  }, [clearSelectedPatientFormFields, resetPatientSearch, clearAthenaProgressNoteEncounter]);

  const handlePatientSearchModeChange = useCallback((modeKey) => {
    if (!changePatientSearchMode(modeKey, clearSelectedPatientFormFields)) return;
    clearAthenaProgressNoteEncounter();

    captureAnalytics('ehr_patient_search_mode_changed', {
      emr: selectedCredential?.emr,
      workflow,
      search_mode: modeKey,
    });
  }, [changePatientSearchMode, clearSelectedPatientFormFields, clearAthenaProgressNoteEncounter, selectedCredential, workflow]);

  useEffect(() => {
    patientSearchResetRef.current = handlePatientSearchReset;
  }, [handlePatientSearchReset]);

  const handlePatientSelect = useCallback((patient) => {
    clearAthenaProgressNoteEncounter();
    selectSearchPatient(patient, mapPatientToFields, (fieldNames) => {
      const fieldsToFlash = fieldNames.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setFlashingFields(fieldsToFlash);
      setTimeout(() => {
        setFlashingFields({});
      }, 700);
    });
  }, [mapPatientToFields, selectSearchPatient, clearAthenaProgressNoteEncounter]);

  const handleClearPatient = useCallback((e) => {
    clearSearchPatient(e, clearSelectedPatientFormFields);
    clearAthenaProgressNoteEncounter();
  }, [clearSearchPatient, clearSelectedPatientFormFields, clearAthenaProgressNoteEncounter]);

  const workflowDisplayTitle = schema?.metadata?.title || modalTitle;
  const workflowDescription = schema?.metadata?.description;
  const nextgenSelectionProofMissing = schema?.emr === 'NextGen'
    && !selectedPatient?.selection_proof;
  const patientSelectionMissing = requiresPatientResultSelection
    && (!selectedPatient || nextgenSelectionProofMissing);
  const athenaEncounter = formValues.custom_fields?.athena_encounter;
  const athenaProgressNoteMissingContext = isAthenaProgressNote && (
    !hasPresentValue(selectedPatient?.value)
    || !hasPresentValue(formValues.location)
    || !hasPresentValue(athenaEncounterProof)
    || !hasPresentValue(athenaEncounter?.encounter_id)
    || !hasPresentValue(athenaEncounter?.department_id)
    || String(athenaEncounter?.department_id) !== String(formValues.location)
  );
  const submitDisabled = fetchingCredentials
    || loading
    || submitting
    || !schema
    || !selectedCredential
    || missingRequiredVisibleField
    || patientSelectionMissing
    || athenaProgressNoteMissingContext
    || Boolean(insuranceValidationError)
    || (nextgenTaskTemplates.blocked && (workflow === 'task_creation' || effectiveTaskRequested));

  // ENG-3934: a provider is requested, or a document set aside as Awaiting
  // Provider, only once everything but the referring provider is filled in, so
  // the document can be finished from what was saved. Until then both actions
  // stay disabled with what's missing.
  const awaitingProviderMissing = [];
  // Picking a patient fills the fields mapped from search; name it once.
  const patientSearchFields = new Set(Object.keys(patientSearchConfig?.field_mappings || {}));
  if (patientSelectionMissing) awaitingProviderMissing.push('a patient from the search results');
  requiredVisibleFields.forEach((field) => {
    if (field.component === 'emr_provider_autocomplete' && field.credential_scoped) return;
    if (patientSelectionMissing && patientSearchFields.has(field.internal_name)) return;
    if (field.component === NEXTGEN_METADATA_COMPONENT) {
      if (!nextgenHoldDraft) awaitingProviderMissing.push('the document type and its required fields');
      return;
    }
    if (!hasPresentValue(formValues[field.internal_name])) awaitingProviderMissing.push(field.label || field.internal_name);
  });
  if (nextgenTaskTemplates.blocked && effectiveTaskRequested) awaitingProviderMissing.push('the task template answers');
  const providerActionsBlockedReason = awaitingProviderMissing.length > 0
    ? `To request a provider or mark this Awaiting Provider, first fill in: ${awaitingProviderMissing.join(', ')}.`
    : null;

  const renderPatientSearch = () => {
    if (!patientSearchEnabled) {
      return null;
    }

    const selectedPatientName = allowResultSelection ? (selectedPatient?.label || selectedPatient?.metadata?.patient_name) : null;
    const selectedPatientMrn = allowResultSelection ? (selectedPatient?.mrn || selectedPatient?.metadata?.mrn) : null;
    const selectedPatientPersonNumber = allowResultSelection ? selectedPatient?.metadata?.person_number : null;
    const selectedPatientDob = allowResultSelection ? extractPatientDob(selectedPatient) : null;
    const selectedMatchLabel = isNextgenVariantSearch ? MATCH_KIND_LABELS[selectedPatient?.match_kind] : null;
    const selectedIsSpeculative = selectedPatient?.speculative === true;
    const patientSearchDescription = patientSearchConfig?.description || (
      allowResultSelection
        ? 'Find an existing patient in the EMR before submitting.'
        : 'Search for existing patients in the EMR. Results are informational only and will not populate the form.'
    );

    const collapseHeader = (
      <div className="dynamic-ehr-drawer__collapse-header">
        <div className="dynamic-ehr-drawer__card-icon">
          <SearchOutlined />
        </div>
        <div className="dynamic-ehr-drawer__card-header-content">
          <div className="dynamic-ehr-drawer__card-title">Search Patients</div>
          {selectedPatientName ? (
            <div
              className="dynamic-ehr-drawer__card-selection"
              ref={selectedPatientSummaryRef}
              tabIndex="-1"
              role="status"
              aria-live="polite"
            >
              <span>
                Selected:
                {' '}
                <strong>{selectedPatientName}</strong>
              </span>
              {selectedMatchLabel && <span className={`dynamic-ehr-drawer__match-badge dynamic-ehr-drawer__match-badge--${selectedPatient.match_kind}`}>{selectedMatchLabel}</span>}
              {isNextgenVariantSearch && (
                <span>
                  DOB:
                  {' '}
                  {selectedPatientDob || 'unavailable'}
                </span>
              )}
              {isNextgenVariantSearch && selectedPatientPersonNumber && (
                <span>
                  Person #:
                  {' '}
                  {selectedPatientPersonNumber}
                </span>
              )}
              {isNextgenVariantSearch && (
                <span className="dynamic-ehr-drawer__card-selection-mrn">
                  MRN:
                  {' '}
                  {selectedPatientMrn || 'unavailable'}
                </span>
              )}
              {selectedIsSpeculative && <span className="dynamic-ehr-drawer__card-selection-warning">Verify this approximate match before submitting.</span>}
              <button
                type="button"
                className="dynamic-ehr-drawer__card-selection-clear"
                onClick={handleClearPatient}
                aria-label="Clear patient selection"
              >
                <CloseOutlined />
              </button>
            </div>
          ) : (
            <div className="dynamic-ehr-drawer__card-description">
              {patientSearchDescription}
            </div>
          )}
        </div>
      </div>
    );

    return (
      <Collapse
        className="dynamic-ehr-drawer__collapse"
        bordered={false}
        expandIconPosition="start"
        defaultActiveKey={['patient-search']}
      >
        <Collapse.Panel header={collapseHeader} key="patient-search">
          <PatientSearchPanel
            searchFields={patientSearchActiveFields}
            searchInstructions={patientSearchConfig?.search_instructions}
            fieldValues={patientFieldValues}
            onFieldChange={handlePatientSearchFieldChange}
            onReset={handlePatientSearchReset}
            results={patientSearchResults}
            isLoading={patientSearchLoading}
            error={patientSearchError}
            canSearch={patientSearchCanSearch}
            allowResultSelection={allowResultSelection}
            onSelect={handlePatientSelect}
            selectedPatient={allowResultSelection ? selectedPatient : null}
            searchWarnings={patientSearchWarnings}
            hasMore={patientSearchHasMore}
            isVariantSearch={isNextgenVariantSearch}
            blockedMessage={nextgenPracticeRequired ? 'Select a NextGen practice before searching.' : null}
            searchModes={patientSearchModes}
            activeMode={patientSearchActiveMode}
            onModeChange={handlePatientSearchModeChange}
            searchTrigger={patientSearchSettings?.searchTrigger}
            onSearch={searchPatients}
          />
          {patientSelectionMissing && (
            <div className="dynamic-ehr-drawer__patient-selection-required" role="status">
              Select a patient from the search results to enable Submit.
            </div>
          )}
        </Collapse.Panel>
      </Collapse>
    );
  };

  // Shown once an existing patient is picked from search (NextGen also needs
  // a practice). The selection proof binds the lookup to that search, so no
  // proof, no list.
  const renderAppointmentHistory = () => {
    const source = appointmentHistorySource(schema?.emr);
    const showHistory = source
      && workflow === 'send_to_chart'
      && hasFeatureInLocalStorage(source.feature)
      && hasPresentValue(selectedPatient?.value)
      && hasPresentValue(selectedPatient?.selection_proof)
      && (!source.needsLocation || hasPresentValue(formValues.location));
    if (!showHistory) return null;

    return (
      <section className="dynamic-ehr-drawer__card dynamic-ehr-drawer__card--appointments" aria-label="Appointment history">
        <div className="dynamic-ehr-drawer__card-header">
          <div className="dynamic-ehr-drawer__card-icon">
            <CalendarCheck size={18} weight="bold" />
          </div>
          <div className="dynamic-ehr-drawer__card-header-content">
            <div className="dynamic-ehr-drawer__card-title">Appointment History</div>
            <div className="dynamic-ehr-drawer__card-description">{source.description}</div>
          </div>
        </div>
        <div className="dynamic-ehr-drawer__card-body">
          <AppointmentHistory
            searchPath={source.searchPath}
            visitNoun={source.visitNoun}
            credentialId={selectedCredential?.id}
            location={source.needsLocation ? formValues.location : undefined}
            patientIdentifier={String(selectedPatient.value)}
            selectionProof={selectedPatient.selection_proof}
          />
        </div>
      </section>
    );
  };

  const renderCredentialSwitcher = () => {
    if (credentials.length <= 1) {
      return null;
    }

    return (
      <Select
        className="dynamic-ehr-drawer__credential-select"
        getPopupContainer={() => document.body}
        dropdownClassName="ehr-select-dropdown"
        value={selectedCredential?.id}
        onChange={(id) => {
          const credential = credentials.find((candidate) => candidate.id === id);
          setSelectedCredential(credential || { id });
        }}
        options={credentials.map((credential) => ({
          value: credential.id,
          label: credential.nickname || credential.emr_name || `Credential ${credential.id}`,
        }))}
      />
    );
  };

  const handleTaskRequestedChange = (e) => {
    const { checked } = e.target;
    setTaskRequested(checked);
    nextgenTaskTemplates.taskToggled(checked);
    setReviewRequested(false);
    if (checked) setArchiveAfterUpload(false);

    if (checked) {
      const defaultsToApply = Object.entries(taskDefaultValues).reduce((acc, [fieldName, defaultValue]) => {
        const currentValue = form.getFieldValue(fieldName);
        if (currentValue === undefined || currentValue === null || currentValue === '') {
          acc[fieldName] = defaultValue;
        }
        return acc;
      }, {});

      if (Object.keys(defaultsToApply).length > 0) {
        applyFieldValues(defaultsToApply);
      }
      return;
    }

    if (taskFieldNames.size === 0) {
      return;
    }

    const clearedValues = Array.from(taskFieldNames).reduce((acc, fieldName) => {
      acc[fieldName] = undefined;
      return acc;
    }, {});
    applyFieldValues(clearedValues);
    setCrossFieldErrors((prevErrors) => (
      prevErrors.filter((err) => !err.fields.some((field) => taskFieldNames.has(field)))
    ));
  };

  const renderTaskToggle = () => {
    if (!embeddedTaskAvailable || isAthenaProgressNote) {
      return null;
    }

    return (
      <div className="dynamic-ehr-drawer__task-toggle">
        <Checkbox checked={taskRequested && !effectiveReviewRequested} onChange={handleTaskRequestedChange}>
          Create Task
        </Checkbox>
        {needsReviewAvailable && (
          <Checkbox
            checked={effectiveReviewRequested}
            onChange={(event) => {
              handleTaskRequestedChange(event);
              setReviewRequested(event.target.checked);
              applyFieldValues({ task_type: undefined });
              captureAnalytics('veradigm_document_review_toggled', { checked: event.target.checked, workflow });
            }}
          >
            Needs Review
          </Checkbox>
        )}
        {effectiveReviewRequested && (
          <p>Send this document to the selected caregiver for review and approval.</p>
        )}
      </div>
    );
  };

  const sectionDescriptions = useMemo(() => schema?.metadata?.section_descriptions || {}, [schema]);
  const submitLabel = schema?.metadata?.submit_label || 'Submit';

  return (
    <Drawer
      title={null}
      visible={visible}
      onClose={handleClose}
      afterVisibleChange={handleAfterVisibleChange}
      destroyOnClose={false}
      forceRender
      width={620}
      closable={false}
      mask={false}
      placement="left"
      className="ehr-side-panel-drawer"
      zIndex={DRAWER_Z_INDEX}
      bodyStyle={{ padding: 0 }}
      footer={(
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {hasFeatureInLocalStorage(ARCHIVE_AFTER_RECORD_ACTIONS_FEATURE) && workflow === 'send_to_chart' && parentType === 'ReceivedFax' && !taskRequested && (
            <Checkbox
              checked={archiveAfterUpload}
              style={{ marginRight: 'auto', alignSelf: 'center' }}
              onChange={(event) => {
                setArchiveAfterUpload(event.target.checked);
                captureAnalytics('chart_archive_after_upload_toggled', { enabled: event.target.checked });
              }}
            >
              Archive after successful upload
            </Checkbox>
          )}
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={submitDisabled}
          >
            {submitLabel}
          </Button>
        </div>
      )}
    >
      {fetchingCredentials || loading || !schema ? (
        <div className="dynamic-ehr-drawer__loading">
          <Spin size="large" tip={fetchingCredentials ? 'Loading credentials...' : 'Loading form...'} />
        </div>
      ) : (
        <div className="dynamic-ehr-drawer">
          <div className="dynamic-ehr-drawer__header">
            <div className="dynamic-ehr-drawer__header-text">
              <div className="dynamic-ehr-drawer__header-title">
                <span className="dynamic-ehr-drawer__header-icon">{getWorkflowIcon(workflow, workflowDisplayTitle)}</span>
                {workflowDisplayTitle}
              </div>
              {workflowDescription && (
                <div className="dynamic-ehr-drawer__header-subtitle">
                  {workflowDescription}
                </div>
              )}
            </div>
            <div className="dynamic-ehr-drawer__header-actions">
              {renderCredentialSwitcher()}
              <button
                type="button"
                className="dynamic-ehr-drawer__close"
                onClick={handleClose}
                aria-label="Close"
              >
                <CloseOutlined />
              </button>
            </div>
          </div>
          <div className="dynamic-ehr-drawer__body">
            {savedForm && (
              <Alert
                type="info"
                showIcon
                className="dynamic-ehr-drawer__saved-form"
                data-testid="awaiting-provider-saved-form"
                message="Filled in from when this was set aside"
                description={`${describeHold(savedForm)} Check the details, and pick the patient again from the search results to confirm.`}
              />
            )}
            {renderPatientSearch()}
            {renderAppointmentHistory()}
            {crossFieldErrors.length > 0 && (
              <Alert
                type="warning"
                showIcon
                className="dynamic-ehr-drawer__cross-field-alert"
                message={crossFieldErrors.map((err) => err.message).join('; ')}
              />
            )}
            {renderTaskToggle()}
            <Form
              form={form}
              layout="vertical"
              className="dynamic-ehr-drawer__form"
              onValuesChange={handleValuesChange}
            >
              {hiddenFields.map((field) => renderField(field))}
              {visibleSectionStructure.map((section) => {
                const sectionTitle = section.title || 'Additional Details';
                const description = sectionDescriptions[section.title] || null;

                return (
                  <section
                    key={section.id}
                    id={section.id}
                    className="dynamic-ehr-drawer__card"
                  >
                    <div className="dynamic-ehr-drawer__card-header">
                      <div className="dynamic-ehr-drawer__card-icon">
                        {getSectionIcon(sectionTitle)}
                      </div>
                      <div className="dynamic-ehr-drawer__card-header-content">
                        <div className="dynamic-ehr-drawer__card-title">{sectionTitle}</div>
                        {description && (
                          <div className="dynamic-ehr-drawer__card-description">{description}</div>
                        )}
                      </div>
                    </div>
                    <div className="dynamic-ehr-drawer__card-body">
                      {isTaskSchemaEntry(section) && nextgenTaskTemplates.templates.length > 0 && (
                        <NextgenTaskTemplateFields
                          answers={nextgenTaskTemplates.answers}
                          categoryUnavailable={nextgenTaskTemplates.categoryUnavailable}
                          credentialId={selectedCredential.id}
                          lines={nextgenTaskTemplates.lines}
                          location={formValues.location ? String(formValues.location) : null}
                          onAnswerChange={nextgenTaskTemplates.setAnswer}
                          onTemplateChange={nextgenTaskTemplates.selectTemplate}
                          template={nextgenTaskTemplates.template}
                          templates={nextgenTaskTemplates.templates}
                          triggerNote={TASK_EMBEDDING_WORKFLOWS.includes(workflow)}
                        />
                      )}
                      {section.subsections.map((subsection) => (
                        <div key={subsection.id} className="dynamic-ehr-drawer__subsection">
                          {subsection.title && (
                            <div className="dynamic-ehr-drawer__subsection-heading">
                              {subsection.title}
                            </div>
                          )}
                          {getSubsectionRows(subsection).map((row, rowIndex) => (
                            renderFieldRow(row, `${subsection.id}-row-${rowIndex}`)
                          ))}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </Form>
          </div>
        </div>
      )}
      <ReferringProviderRequestModal
        visible={providerRequestSearch !== null && Boolean(providerRequestConfig)}
        credentialId={providerRequestCredentialId}
        config={providerRequestConfig}
        searchText={providerRequestSearch || ''}
        personNumber={selectedPatient?.metadata?.person_number}
        workflow={workflow}
        canMarkAwaitingProvider={canMarkAwaitingProvider}
        onSent={({ markAwaitingProvider: mark, requestedProvider } = {}) => {
          setProviderRequestSent(true);
          if (mark) markAwaitingProvider('request_form', requestedProvider);
          else if (awaitingProviderHold) markAwaitingProvider('request_form_update', requestedProvider);
        }}
        onClose={() => setProviderRequestSearch(null)}
      />
    </Drawer>
  );
};

DynamicEhrDrawer.propTypes = {
  visible: PropTypes.bool,
  drawerData: PropTypes.shape({
    emrIntegrationCredentialId: PropTypes.number,
    workflow: PropTypes.string,
    prefill: PropTypes.object,
    parentType: PropTypes.string,
    sendToken: PropTypes.string,
    isSentContext: PropTypes.bool,
    title: PropTypes.string,
    providerHints: PropTypes.shape({
      orderingProviderName: PropTypes.string,
      referringProviderName: PropTypes.string,
    }),
  }),
  onClose: PropTypes.func.isRequired,
  createChartUpload: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  allDocumentCategories: PropTypes.object,
  requestInboxTableRefresh: PropTypes.func,
};

DynamicEhrDrawer.defaultProps = {
  visible: false,
  drawerData: null,
  allDocumentCategories: {},
  requestInboxTableRefresh: () => {},
};

export { DynamicEhrDrawer as UnconnectedDynamicEhrDrawer };

const mapStateToProps = (state) => ({
  allDocumentCategories: state.userProfile.allDocumentCategories,
});

export default connect(mapStateToProps, {
  createChartUpload,
  getDocumentCategories,
  requestInboxTableRefresh,
})(DynamicEhrDrawer);
